import React, { useState, useEffect, useRef } from 'react';
import { SearchIcon, CloseIcon } from './Icons';
import './SearchBar.css';

// Helper pour supprimer les accents et mettre en minuscule
const normalizeText = (str) => str ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';

const SearchBar = ({ searchTerm, setSearchTerm, sounds = [], selectedCategory, setSelectedCategory }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [searchHistory, setSearchHistory] = useState([]);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Charger l'historique de recherche depuis localStorage
  useEffect(() => {
    const history = JSON.parse(localStorage.getItem('soundnest-search-history') || '[]');
    setSearchHistory(history.slice(0, 5)); // Garder seulement les 5 dernières
  }, []);

  // Sauvegarder dans l'historique
  const saveToHistory = (term) => {
    if (!term.trim() || term.length < 2) return;
    
    const newHistory = [term, ...searchHistory.filter(h => h !== term)].slice(0, 5);
    setSearchHistory(newHistory);
    localStorage.setItem('soundnest-search-history', JSON.stringify(newHistory));
  };

  // Fonction de scoring pour la pertinence
  const calculateRelevance = (text, query) => {
    const textNorm = normalizeText(text);
    const queryNorm = normalizeText(query);
    if (!textNorm || !queryNorm) return 0;
    
    // Score exact match
    if (textNorm === queryNorm) return 100;
    
    // Score début de mot
    if (textNorm.startsWith(queryNorm)) return 80;
    
    // Score match exact dans un mot
    const words = textNorm.split(/\s+/);
    if (words.some(word => word.startsWith(queryNorm))) return 70;
    
    // Score contient la séquence
    if (textNorm.includes(queryNorm)) return 50;
    
    // Score fuzzy (lettres dans l'ordre)
    let fuzzyScore = 0;
    let queryIndex = 0;
    for (let char of textNorm) {
      if (queryIndex < queryNorm.length && char === queryNorm[queryIndex]) {
        fuzzyScore += 10;
        queryIndex++;
      }
    }
    if (queryIndex === queryNorm.length) return Math.min(fuzzyScore, 40);
    
    return 0;
  };

  // Générer des suggestions intelligentes avec scoring et optimisation
  const generateSuggestions = (query) => {
    if (!query.trim()) {
      // Suggestions populaires quand pas de recherche
      const popularSuggestions = [
        { text: 'eau', type: 'popular', category: 'Populaire' },
        { text: 'nature', type: 'popular', category: 'Populaire' },
        { text: 'musique', type: 'popular', category: 'Populaire' },
        { text: 'explosion', type: 'popular', category: 'Populaire' },
        { text: 'piano', type: 'popular', category: 'Populaire' }
      ];
      
      // Ajouter l'historique
      const historyItems = searchHistory.map(item => ({
        text: item,
        type: 'history',
        category: 'Historique'
      }));
      
      return [...historyItems, ...popularSuggestions].slice(0, 6);
    }
    
    // Limiter le nombre de sons traités pour éviter les lags
    const maxSoundsToProcess = Math.min(sounds.length, 500);
    const soundsToProcess = sounds.slice(0, maxSoundsToProcess);

    const allSuggestions = [];
    
    // Parcourir les sons limités pour générer des suggestions
    soundsToProcess.forEach(sound => {
      const title = sound?.title || '';
      const author = sound?.author || '';
      const tags = sound?.tags || [];
      const category = sound?.categorie || '';
      
      // Score pour le titre
      const titleScore = calculateRelevance(title, query);
      if (titleScore > 0) {
        allSuggestions.push({
          text: title,
          type: 'title',
          author: author,
          category: category,
          score: titleScore,
          source: sound
        });
      }
      
      // Score pour les tags
      tags.forEach(tag => {
        const tagScore = calculateRelevance(tag, query);
        if (tagScore > 0) {
          allSuggestions.push({
            text: tag,
            type: 'tag',
            category: category,
            score: tagScore * 0.8, // Poids réduit pour les tags
            source: sound
          });
        }
      });
      
      // Score pour l'auteur
      const authorScore = calculateRelevance(author, query);
      if (authorScore > 0) {
        allSuggestions.push({
          text: author,
          type: 'author',
          category: 'Auteur',
          score: authorScore * 0.7, // Poids réduit pour les auteurs
          source: sound
        });
      }
      
      // Score pour la catégorie
      const categoryScore = calculateRelevance(category, query);
      if (categoryScore > 0) {
        allSuggestions.push({
          text: category,
          type: 'category',
          category: 'Catégorie',
          score: categoryScore * 0.6, // Poids réduit pour les catégories
          source: sound
        });
      }
    });
    
    // Trier par score et prendre les meilleurs (optimisé)
    const sortedSuggestions = allSuggestions
      .filter(s => s.score >= 35) // Seuil plus strict pour moins de résultats
      .sort((a, b) => b.score - a.score)
      .slice(0, 12); // Pré-limiter avant déduplication
    
    // Supprimer les doublons et limiter à 6 résultats
    const uniqueResults = [];
    const seen = new Set();
    
    for (const suggestion of sortedSuggestions) {
      if (!seen.has(suggestion.text.toLowerCase()) && uniqueResults.length < 6) {
        seen.add(suggestion.text.toLowerCase());
        uniqueResults.push(suggestion);
      }
    }
    
    return uniqueResults;
  };

  // Mettre à jour les suggestions quand le terme change
  useEffect(() => {
    // Debounce pour éviter trop de calculs lors de la frappe
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        const newSuggestions = generateSuggestions(searchTerm);
        setSuggestions(newSuggestions);
      } else {
        setSuggestions(generateSuggestions(''));
      }
    }, 100); // Attendre 100ms après la dernière frappe
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, sounds, searchHistory]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(true);
    setActiveSuggestion(-1);
    
    // Debounce pour éviter trop de calculs
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Rediriger vers "Découvrir" si on est dans une catégorie spécifique
      if (selectedCategory && selectedCategory !== 'all' && value.trim()) {
        setSelectedCategory('all');
      }
    }, 150);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.text);
    setShowSuggestions(false);
    saveToHistory(suggestion.text);
    inputRef.current?.blur();
    
    // Rediriger vers "Découvrir" si on est dans une catégorie spécifique
    if (selectedCategory && selectedCategory !== 'all') {
      setSelectedCategory('all');
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveSuggestion(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveSuggestion(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (activeSuggestion >= 0) {
          handleSuggestionClick(suggestions[activeSuggestion]);
        } else if (searchTerm.trim()) {
          saveToHistory(searchTerm);
          setShowSuggestions(false);
          // Rediriger vers "Découvrir" si on est dans une catégorie spécifique
          if (selectedCategory && selectedCategory !== 'all') {
            setSelectedCategory('all');
          }
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setActiveSuggestion(-1);
        break;
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setShowSuggestions(false);
    setActiveSuggestion(-1);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setShowSuggestions(true);
  };

  const handleBlur = (e) => {
    // Délai pour permettre le clic sur les suggestions
    setTimeout(() => {
      if (!suggestionsRef.current?.contains(e.relatedTarget)) {
        setShowSuggestions(false);
        setActiveSuggestion(-1);
      }
    }, 150);
  };

  const getSuggestionIcon = (type) => {
    switch (type) {
      case 'title': return <span className="suggestion-icon-svg"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3" fill="currentColor"/><circle cx="18" cy="16" r="3" fill="currentColor"/></svg></span>;
      case 'author': return <span className="suggestion-icon-svg"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>;
      case 'tag': return <span className="suggestion-icon-svg"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg></span>;
      case 'category': return <span className="suggestion-icon-svg"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg></span>;
      case 'history': return <span className="suggestion-icon-svg"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></span>;
      case 'popular': return <span className="suggestion-icon-svg"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg></span>;
      default: return <span className="suggestion-icon-svg"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg></span>;
    }
  };

  return (
    <div className="search-bar">
      <div className="search-input-container">
        <span className="search-icon"><SearchIcon /></span>
        <input
          ref={inputRef}
          type="text"
          placeholder="Rechercher des sons... (titre, auteur, tags, catégorie)"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="search-input"
          autoComplete="off"
        />
        {searchTerm && (
          <button 
            className="clear-button"
            onClick={handleClear}
            title="Effacer la recherche"
          >
            <CloseIcon />
          </button>
        )}
        
        {showSuggestions && suggestions.length > 0 && (
          <div ref={suggestionsRef} className="suggestions-dropdown">
            {suggestions.map((suggestion, index) => (
              <div
                key={`${suggestion.type}-${suggestion.text}-${index}`}
                className={`suggestion-item ${index === activeSuggestion ? 'active' : ''}`}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <span className="suggestion-icon">
                  {getSuggestionIcon(suggestion.type)}
                </span>
                <div className="suggestion-content">
                  <span className="suggestion-text">{suggestion.text}</span>
                  {suggestion.author && suggestion.type === 'title' && (
                    <span className="suggestion-author">par {suggestion.author}</span>
                  )}
                  <span className="suggestion-category">{suggestion.category}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;
